import React, { useEffect, useMemo, memo, useState } from 'react';
import Map, { Marker, Popup, Source, Layer, NavigationControl, ScaleControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography, Chip, Button, Divider, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import {
  Satellite as SatelliteIcon,
  OpenInNew as OpenInNewIcon,
  Map as MapIcon,
  Terrain as TerrainIcon,
  Layers as LayersIcon,
  TrendingFlat as DisplacementIcon,
  Speed as VelocityIcon,
  BatteryFull as BatteryFullIcon,
  Battery60 as BatteryMedIcon,
  Battery20 as BatteryLowIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Stili mappa disponibili
const MAP_STYLES = {
  streets: {
    name: 'Stradale',
    url: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
    icon: MapIcon,
  },
  satellite: {
    name: 'Satellite',
    // Stile personalizzato con tiles Esri World Imagery
    url: {
      version: 8,
      sources: {
        'esri-satellite': {
          type: 'raster',
          tiles: [
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          ],
          tileSize: 256,
          attribution: '&copy; Esri, Maxar, Earthstar Geographics',
        },
      },
      layers: [
        {
          id: 'esri-satellite-layer',
          type: 'raster',
          source: 'esri-satellite',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    },
    icon: SatelliteIcon,
  },
  eagle_fvg: {
    name: 'Eagle FVG',
    // Ortofoto Regione FVG via proxy backend (per aggirare CORS)
    url: {
      version: 8,
      sources: {
        'eagle-fvg': {
          type: 'raster',
          tiles: [
            '/api/wms/eagle-fvg?BBOX={bbox-epsg-3857}&WIDTH=256&HEIGHT=256'
          ],
          tileSize: 256,
          attribution: '&copy; Regione FVG - IRDAT',
        },
      },
      layers: [
        {
          id: 'eagle-fvg-layer',
          type: 'raster',
          source: 'eagle-fvg',
          minzoom: 0,
          maxzoom: 22,
        },
      ],
    },
    icon: SatelliteIcon,
  },
  terrain: {
    name: 'Terreno',
    url: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
    icon: TerrainIcon,
  },
  dark: {
    name: 'Scuro',
    url: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
    icon: LayersIcon,
  },
};

// Componente marker personalizzato con etichetta visibile
const SensorMarker = memo(function SensorMarker({ status, name, deltaUp }) {
  const colors = {
    online: '#4caf50',
    offline: '#9e9e9e',
    warning: '#ff9800',
    alarm: '#f44336',
    ONLINE: '#4caf50',
    OFFLINE: '#9e9e9e',
    WARNING: '#ff9800',
    ALARM: '#f44336',
  };

  const color = colors[status] || colors.offline;
  const isAnimated = status === 'alarm' || status === 'ALARM' || status === 'warning' || status === 'WARNING';

  // Indicatore movimento verticale (soglia 1mm)
  const verticalIndicator = deltaUp != null && Math.abs(deltaUp) > 0.001
    ? { symbol: deltaUp > 0 ? '▲' : '▼', color: deltaUp > 0 ? '#4caf50' : '#ff9800' }
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
      {/* Etichetta nome sensore */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '4px 10px',
          borderRadius: 4,
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 4,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          borderLeft: `4px solid ${color}`,
        }}
      >
        {name}
        {verticalIndicator && (
          <span style={{ color: verticalIndicator.color, fontSize: 10, lineHeight: 1 }}>
            {verticalIndicator.symbol}
          </span>
        )}
      </div>
      {/* Marker circolare */}
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: '50%',
          backgroundColor: color,
          border: '3px solid white',
          boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
          animation: isAnimated ? 'pulse 1.5s infinite' : 'none',
        }}
      />
      {/* Freccia puntatore */}
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: `6px solid ${color}`,
          marginTop: -1,
        }}
      />
    </div>
  );
});

// Popup sensore compatto (memoizzato)
const SensorPopupContent = memo(function SensorPopupContent({ sensor, displacement, onViewDetails }) {
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    const colors = {
      online: 'success',
      offline: 'default',
      warning: 'warning',
      alarm: 'error',
    };
    return colors[statusLower] || 'default';
  };

  const formatDisplacement = (value) => {
    if (!value) return 'N/A';
    return `${(value * 1000).toFixed(1)} mm`;
  };

  const getBatteryIcon = (level) => {
    if (level === null || level === undefined) return <BatteryFullIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
    if (level > 60) return <BatteryFullIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
    if (level > 30) return <BatteryMedIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
    return <BatteryLowIcon sx={{ color: '#f44336', fontSize: 20 }} />;
  };

  return (
    <Box className="sensor-popup" sx={{ width: 260, p: 1.5 }}>
      {/* Header con nome e stato */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'primary.main' }}>
          {sensor.name}
        </Typography>
        <Chip
          label={sensor.status?.toUpperCase() || 'N/A'}
          color={getStatusColor(sensor.status)}
          size="small"
          sx={{ height: 20, fontSize: 10 }}
        />
      </Box>

      {/* Info sensore: solo batteria */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 1.5,
        py: 0.5,
        px: 1,
        bgcolor: 'action.hover',
        borderRadius: 1,
      }}>
        {getBatteryIcon(sensor.status?.toLowerCase() === 'offline' ? null : sensor.battery_level)}
        <Typography variant="caption" fontWeight={500}>
          {sensor.status?.toLowerCase() === 'offline' ? '---' : `${sensor.battery_level || 0}%`}
        </Typography>
      </Box>

      {/* Spostamento nelle tre direzioni */}
      {displacement ? (
        <Box sx={{
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 1,
          p: 1.5,
          mb: 1.5,
        }}>
          <Typography variant="caption" sx={{ opacity: 0.9, display: 'block', mb: 0.5, textAlign: 'center' }}>
            Spostamento
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.85, minWidth: 24 }}>N</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {formatDisplacement(displacement.delta_north)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.85, minWidth: 24 }}>E</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {formatDisplacement(displacement.delta_east)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.85, minWidth: 24 }}>U</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                {formatDisplacement(displacement.delta_up)}
              </Typography>
            </Box>
          </Box>
          {displacement.azimuth !== undefined && (
            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', textAlign: 'center', mt: 0.5 }}>
              {displacement.azimuth?.toFixed(0)}° N
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1.5, mb: 1.5, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            In attesa di dati
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        size="small"
        fullWidth
        startIcon={<OpenInNewIcon />}
        onClick={onViewDetails}
      >
        Dettagli
      </Button>
    </Box>
  );
});

// Componente principale mappa
function SensorMap({
  sensors = [],
  displacements = [],
  selectedSensor = null,
  center = [9.19, 45.4642], // [lng, lat] per MapLibre
  zoom = 14,
  showArrows = true,
  showLegend = true,
  showAttribution = true,
  height = '100%',
}) {
  const navigate = useNavigate();
  const [popupInfo, setPopupInfo] = useState(null);
  const [mapStyle, setMapStyle] = useState('streets');
  const [arrowMode, setArrowMode] = useState('displacement'); // 'displacement' o 'velocity'
  const [viewState, setViewState] = useState({
    longitude: center[0],
    latitude: center[1],
    zoom: zoom,
  });

  // Imposta centro mappa solo al primo caricamento o quando viene selezionato un sensore
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Centra su sensore selezionato
    if (selectedSensor) {
      const lat = selectedSensor.current_latitude || selectedSensor.initial_latitude;
      const lon = selectedSensor.current_longitude || selectedSensor.initial_longitude;
      if (lat && lon) {
        setViewState(prev => ({ ...prev, longitude: lon, latitude: lat, zoom: 17 }));
      }
      return;
    }

    // Solo al primo caricamento, centra sui sensori
    if (!initialized && sensors.length > 0) {
      const validSensors = sensors.filter(
        (s) => (s.current_latitude || s.initial_latitude) && (s.current_longitude || s.initial_longitude)
      );
      if (validSensors.length > 0) {
        const avgLat = validSensors.reduce((sum, s) => sum + (s.current_latitude || s.initial_latitude), 0) / validSensors.length;
        const avgLon = validSensors.reduce((sum, s) => sum + (s.current_longitude || s.initial_longitude), 0) / validSensors.length;
        setViewState(prev => ({ ...prev, longitude: avgLon, latitude: avgLat, zoom: 17 }));
        setInitialized(true);
      }
    }
  }, [sensors, selectedSensor, initialized]);

  // Crea mappa spostamenti per ID sensore
  const displacementMap = useMemo(() => {
    const map = {};
    displacements.forEach((d) => {
      map[d.sensor_id] = d;
    });
    return map;
  }, [displacements]);

  // GeoJSON per le frecce di spostamento o velocità (zoom-adaptive)
  const arrowsGeoJson = useMemo(() => {
    if (!showArrows) return null;

    // Costanti per conversione coordinate
    const LAT_M_PER_DEG = 111132.92;
    const LON_M_PER_DEG = 111412.84 * Math.cos(Math.PI * 46 / 180); // ~46° lat

    const TARGET_PIXELS = 30; // lunghezza massima freccia in pixel
    const MIN_ARROW_PX = 3;   // sotto questa soglia la freccia non viene disegnata

    // Metri per pixel al livello di zoom corrente
    const latRad = (viewState.latitude || 46) * Math.PI / 180;
    const metersPerPixel = 156543.03 * Math.cos(latRad) / Math.pow(2, viewState.zoom);

    // Prima passata: raccogli tutti i vettori con la loro magnitudine
    const vectors = [];
    sensors.forEach((sensor) => {
      const displacement = displacementMap[sensor.id];
      if (!displacement) return;

      const startLat = sensor.current_latitude || sensor.initial_latitude;
      const startLon = sensor.current_longitude || sensor.initial_longitude;
      if (!startLat || !startLon) return;

      let deltaN, deltaE, magnitude, color;

      if (arrowMode === 'velocity') {
        deltaN = displacement.velocity_north || 0;
        deltaE = displacement.velocity_east || 0;
        magnitude = displacement.total_velocity || 0;

        color = '#4caf50';
        if (magnitude > 0.0005) color = '#ff9800';
        if (magnitude > 0.002) color = '#f44336';
      } else {
        deltaN = displacement.delta_north;
        deltaE = displacement.delta_east;
        magnitude = displacement.total_displacement || displacement.total || 0;
        if (deltaN === undefined || deltaN === null) return;

        color = '#4caf50';
        if (magnitude > 0.02) color = '#ff9800';
        if (magnitude > 0.05) color = '#f44336';
      }

      const vecMagnitude = Math.sqrt(deltaN * deltaN + deltaE * deltaE);
      if (vecMagnitude < 1e-8) return;

      vectors.push({ startLat, startLon, deltaN, deltaE, vecMagnitude, color });
    });

    if (vectors.length === 0) return null;

    // Scala dinamica: la freccia più lunga = TARGET_PIXELS pixel
    const maxMagnitude = Math.max(...vectors.map(v => v.vecMagnitude));
    if (maxMagnitude < 1e-8) return null;

    const maxArrowMeters = TARGET_PIXELS * metersPerPixel;
    const dynamicScale = maxArrowMeters / maxMagnitude;
    const minArrowMeters = MIN_ARROW_PX * metersPerPixel;

    const lineFeatures = [];
    const arrowHeadFeatures = [];

    vectors.forEach(({ startLat, startLon, deltaN, deltaE, vecMagnitude, color }) => {
      if (vecMagnitude * dynamicScale < minArrowMeters) return;

      const lat_offset = (deltaN * dynamicScale) / LAT_M_PER_DEG;
      const lon_offset = (deltaE * dynamicScale) / LON_M_PER_DEG;

      const endLat = startLat + lat_offset;
      const endLon = startLon + lon_offset;

      lineFeatures.push({
        type: 'Feature',
        properties: { color },
        geometry: {
          type: 'LineString',
          coordinates: [[startLon, startLat], [endLon, endLat]],
        },
      });

      // Punta della freccia
      const angle = Math.atan2(lat_offset, lon_offset);
      const lineLength = Math.sqrt(lat_offset * lat_offset + lon_offset * lon_offset);
      const arrowHeadLength = lineLength * 0.15;
      const arrowHeadAngle = Math.PI / 8;

      const tip1Lon = endLon - arrowHeadLength * Math.cos(angle - arrowHeadAngle);
      const tip1Lat = endLat - arrowHeadLength * Math.sin(angle - arrowHeadAngle);
      const tip2Lon = endLon - arrowHeadLength * Math.cos(angle + arrowHeadAngle);
      const tip2Lat = endLat - arrowHeadLength * Math.sin(angle + arrowHeadAngle);

      arrowHeadFeatures.push({
        type: 'Feature',
        properties: { color },
        geometry: {
          type: 'Polygon',
          coordinates: [[[endLon, endLat], [tip1Lon, tip1Lat], [tip2Lon, tip2Lat], [endLon, endLat]]],
        },
      });
    });

    return {
      lines: { type: 'FeatureCollection', features: lineFeatures },
      arrowHeads: { type: 'FeatureCollection', features: arrowHeadFeatures },
    };
  }, [sensors, displacementMap, showArrows, arrowMode, viewState.zoom, viewState.latitude]);

  return (
    <Box sx={{ height, width: '100%', position: 'relative' }}>
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
          /* Controlli zoom centrati verticalmente a sinistra */
          .maplibregl-ctrl-bottom-left {
            top: 50% !important;
            bottom: auto !important;
            transform: translateY(-50%) !important;
            left: 10px !important;
          }
          /* Scala in basso a destra */
          .maplibregl-ctrl-bottom-right {
            left: 50% !important;
            right: auto !important;
            transform: translateX(-50%) !important;
            bottom: 10px !important;
          }
          ${!showAttribution ? `
          /* Nasconde l'attribution */
          .maplibregl-ctrl-attrib {
            display: none !important;
          }
          ` : ''}
        `}
      </style>
      <Map
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLES[mapStyle].url}
      >
        <NavigationControl position="bottom-left" />
        <ScaleControl position="bottom-right" unit="metric" />

        {/* Layer frecce spostamento - linee */}
        {arrowsGeoJson && arrowsGeoJson.lines.features.length > 0 && (
          <>
            <Source id="arrows-lines" type="geojson" data={arrowsGeoJson.lines}>
              <Layer
                id="arrows-line"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': 2,
                  'line-opacity': 0.9,
                }}
              />
            </Source>
            <Source id="arrows-heads" type="geojson" data={arrowsGeoJson.arrowHeads}>
              <Layer
                id="arrows-head"
                type="fill"
                paint={{
                  'fill-color': ['get', 'color'],
                  'fill-opacity': 0.9,
                }}
              />
            </Source>
          </>
        )}

        {/* Markers sensori */}
        {sensors.map((sensor) => {
          const lat = sensor.current_latitude || sensor.initial_latitude;
          const lon = sensor.current_longitude || sensor.initial_longitude;

          if (!lat || !lon) return null;

          return (
            <Marker
              key={sensor.id}
              longitude={lon}
              latitude={lat}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                setPopupInfo(sensor);
              }}
            >
              <SensorMarker status={sensor.status} name={sensor.name} deltaUp={displacementMap[sensor.id]?.delta_up} />
            </Marker>
          );
        })}

        {/* Popup */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.current_longitude || popupInfo.initial_longitude}
            latitude={popupInfo.current_latitude || popupInfo.initial_latitude}
            anchor="bottom"
            onClose={() => setPopupInfo(null)}
            closeOnClick={false}
            maxWidth="320px"
          >
            <SensorPopupContent
              sensor={popupInfo}
              displacement={displacementMap[popupInfo.id]}
              onViewDetails={() => {
                setPopupInfo(null);
                navigate(`/sensor/${popupInfo.id}`);
              }}
            />
          </Popup>
        )}
      </Map>

      {/* Selettore stile mappa - in basso a sinistra, allineato con scala */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 10,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 2,
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <ToggleButtonGroup
          value={mapStyle}
          exclusive
          onChange={(e, value) => value && setMapStyle(value)}
          size="small"
          sx={{ display: 'flex' }}
        >
          {Object.entries(MAP_STYLES).map(([key, style]) => {
            const IconComponent = style.icon;
            return (
              <Tooltip key={key} title={style.name} placement="top">
                <ToggleButton
                  value={key}
                  sx={{
                    px: 1.5,
                    py: 0.8,
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                    },
                  }}
                >
                  <IconComponent fontSize="small" />
                </ToggleButton>
              </Tooltip>
            );
          })}
        </ToggleButtonGroup>
      </Box>

      {/* Legenda - in basso a destra */}
      {showLegend && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 1.5,
            boxShadow: 2,
            zIndex: 1000,
          }}
        >
          <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
            Stato sensori
          </Typography>
          {[
            { color: '#4caf50', label: 'Online' },
            { color: '#ff9800', label: 'Warning' },
            { color: '#f44336', label: 'Alarm' },
            { color: '#9e9e9e', label: 'Offline' },
          ].map(({ color, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: color }} />
              <Typography variant="caption">{label}</Typography>
            </Box>
          ))}

          <Divider sx={{ my: 1 }} />

          {/* Selettore modalità frecce */}
          <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
            Frecce
          </Typography>
          <ToggleButtonGroup
            value={arrowMode}
            exclusive
            onChange={(e, value) => value && setArrowMode(value)}
            size="small"
            sx={{ display: 'flex' }}
          >
            <Tooltip title="Spostamento dal riferimento" placement="bottom">
              <ToggleButton
                value="displacement"
                sx={{
                  px: 1,
                  py: 0.5,
                  fontSize: 10,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                }}
              >
                <DisplacementIcon fontSize="small" sx={{ mr: 0.5 }} />
                Spost.
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Velocità stimata (mm/giorno)" placement="bottom">
              <ToggleButton
                value="velocity"
                sx={{
                  px: 1,
                  py: 0.5,
                  fontSize: 10,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                  },
                }}
              >
                <VelocityIcon fontSize="small" sx={{ mr: 0.5 }} />
                Vel.
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      )}
    </Box>
  );
}

export default memo(SensorMap);
